'use client';

import Link from 'next/link';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LandingTrendCard } from '@/lib/landing-config';

function TrendArrow({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      width="47"
      height="7"
      viewBox="0 0 47 7"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ transform: direction === 'right' ? 'scaleX(-1)' : undefined }}
    >
      <path
        d="M5.4386 5.07825L3.14561 3.51909L46.7539 3.51787V2.29787L3.41889 2.29909L5.5789 1.05774L4.97134 0L0.00044632 2.85602L4.75235 6.08719L5.4386 5.07825Z"
        fill="white"
      />
    </svg>
  );
}

const OFFSET_CLASSES = [
  'md:translate-y-0',
  'md:translate-y-[120px]',
  'md:-translate-y-[40px]',
];

export function TrendsCarousel({ cards }: { cards: LandingTrendCard[] }) {
  const total = cards.length;
  const [start, setStart] = useState(0);
  const [dir, setDir] = useState<'next' | 'prev'>('next');
  const [animKey, setAnimKey] = useState(0);
  const lockRef = useRef(false);

  const visible = useMemo<LandingTrendCard[]>(() => {
    if (total === 0) return [];
    return [0, 1, 2]
      .map((i) => cards[(start + i) % total])
      .filter((c): c is LandingTrendCard => Boolean(c));
  }, [cards, start, total]);

  const advance = useCallback(
    (step: 1 | -1) => {
      if (total <= 3 || lockRef.current) return;
      lockRef.current = true;
      setDir(step === 1 ? 'next' : 'prev');
      setStart((s) => (s + step + total) % total);
      setAnimKey((k) => k + 1);
      window.setTimeout(() => {
        lockRef.current = false;
      }, 700);
    },
    [total],
  );

  useEffect(() => {
    if (total <= 3) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') advance(1);
      if (e.key === 'ArrowLeft') advance(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [advance, total]);

  const canCycle = total > 3;

  return (
    <section id="trends" className="relative overflow-hidden bg-black">
      <div className="mx-auto grid max-w-[1440px] gap-12 px-5 pb-28 pt-24 md:grid-cols-[320px_1fr] md:gap-16 md:px-[124px] md:pb-48 md:pt-40">
        <div className="flex flex-col gap-7 md:self-center">
          <div className="flex flex-col gap-4">
            <p
              className="text-base text-white md:text-lg"
              style={{ letterSpacing: '-0.4px', lineHeight: 1.6 }}
            >
              Создать контент — просто
            </p>
            <h2 className="font-display text-5xl font-bold leading-none text-white md:text-[80px]">
              Тренды
            </h2>
          </div>
          <Link
            href="/trends"
            className="inline-flex h-[60px] w-full max-w-[221px] items-center justify-center rounded-[20px] bg-white px-6 text-lg font-semibold text-[#222326] shadow-[0_8px_24px_-12px_rgba(0,0,0,0.4)] transition-transform hover:-translate-y-0.5"
          >
            Начать творить
          </Link>
          <div className="mt-2 flex items-center gap-4">
            <button
              type="button"
              onClick={() => advance(-1)}
              aria-label="Предыдущие тренды"
              disabled={!canCycle}
              className="flex h-[61px] w-[61px] items-center justify-center rounded-full transition-[opacity,transform] duration-300 hover:-translate-x-0.5 hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-25"
            >
              <TrendArrow direction="left" />
            </button>
            <button
              type="button"
              onClick={() => advance(1)}
              aria-label="Следующие тренды"
              disabled={!canCycle}
              className="flex h-[61px] w-[61px] items-center justify-center rounded-full transition-[opacity,transform] duration-300 hover:translate-x-0.5 hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-25"
            >
              <TrendArrow direction="right" />
            </button>
          </div>
        </div>

        <div className="-mx-5 overflow-x-auto pb-6 md:mx-0 md:overflow-visible">
          <div
            key={animKey}
            className="flex min-w-max items-start gap-6 px-5 md:min-w-0 md:grid md:grid-cols-3 md:px-0"
          >
            {visible.map((card, idx) => {
              const isLink = !card.id.startsWith('fb-');
              const Wrapper: React.ElementType = isLink ? Link : 'article';
              const wrapperProps = isLink ? { href: `/trends/${card.id}` } : {};
              return (
                <Wrapper
                  key={`${animKey}-${card.id}`}
                  {...wrapperProps}
                  className={`relative w-[260px] shrink-0 md:w-auto ${OFFSET_CLASSES[idx]}`}
                >
                  <div
                    data-dir={dir === 'prev' ? 'prev' : 'next'}
                    style={
                      {
                        '--trend-i': idx,
                        borderRadius: '42px 8px 42px 8px',
                        aspectRatio: '2 / 3',
                      } as React.CSSProperties
                    }
                    className="trend-card-anim group relative overflow-hidden bg-[#1a1a1d]"
                  >
                    {card.imageUrl ? (
                      /* eslint-disable-next-line @next/next/no-img-element */
                      <img
                        src={card.imageUrl}
                        alt={card.name}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                      />
                    ) : (
                      <div
                        className={`absolute inset-0 bg-gradient-to-br ${card.accent ?? 'from-[#ef6444]/40'} via-[#1a1a1d] to-[#080809]`}
                      />
                    )}
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[239px] bg-gradient-to-t from-black/85 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-[21px]">
                      <h3 className="font-display text-center text-2xl font-normal text-white md:text-[33px]">
                        {card.name}
                      </h3>
                    </div>
                  </div>
                </Wrapper>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
